

import { useState, useRef, useEffect } from "react";

interface SearchableDropdownProps {
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  allowAddNew?: boolean;
  onAddNew?: (newValue: string) => void;
  error?: string;
  disabled?: boolean;
}

export default function SearchableDropdown({
  options,
  value,
  onChange,
  placeholder = "Select or search...",
  label,
  allowAddNew = false,
  onAddNew,
  error,
  disabled = false,
}: SearchableDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedOption = options.find((opt) => opt.value === value);

  const filteredOptions = options.filter((opt) =>
    opt.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const showAddNew =
    allowAddNew &&
    searchQuery &&
    !options.some(
      (opt) => opt.label.toLowerCase() === searchQuery.toLowerCase()
    );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchQuery("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearchQuery("");
  };

  const handleAddNew = () => {
    if (onAddNew && searchQuery) {
      onAddNew(searchQuery);
      setSearchQuery("");
      setIsOpen(false);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      {label && (
        <label className="block text-xs font-medium text-stone-500 mb-1.5">
          {label}
        </label>
      )}

      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`
          w-full px-3 py-2.5 rounded-xl border text-sm text-left transition-colors flex items-center justify-between
          ${error
            ? "border-rose-300 bg-rose-50"
            : "border-stone-200 bg-white hover:border-stone-300"
          }
          ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
          outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100
        `}
      >
        <span className={selectedOption ? "text-stone-800" : "text-stone-400"}>
          {selectedOption?.label || placeholder}
        </span>
        <svg
          className={`w-4 h-4 text-stone-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {error && <p className="text-xs text-rose-500 mt-1">{error}</p>}

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white rounded-xl border border-stone-200 shadow-lg max-h-60 overflow-hidden">
          <div className="p-2 border-b border-stone-100">
            <div className="relative">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                ref={inputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search..."
                className="w-full pl-9 pr-3 py-2 text-sm bg-stone-50 rounded-lg border-none outline-none focus:ring-2 focus:ring-amber-100"
              />
            </div>
          </div>

          <div className="overflow-y-auto max-h-48">
            {filteredOptions.length === 0 && !showAddNew && (
              <div className="px-3 py-4 text-sm text-stone-400 text-center">
                No options found
              </div>
            )}

            {filteredOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleSelect(option.value)}
                className={`
                  w-full px-3 py-2.5 text-left text-sm transition-colors flex items-center justify-between
                  ${option.value === value
                    ? "bg-amber-50 text-amber-700"
                    : "hover:bg-stone-50 text-stone-700"
                  }
                `}
              >
                <span>{option.label}</span>
                {option.value === value && (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
            ))}

            {showAddNew && (
              <button
                type="button"
                onClick={handleAddNew}
                className="w-full px-3 py-2.5 text-left text-sm bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add "{searchQuery}"
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
